import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api, ApiError, errorText } from '../api';
import { parseEuro } from '../lib/money';
import { formInfo, RECHTSFORMEN } from './rechtsform';
import { NurLesen } from './tarifHinweis';
import type { Absender, Rechtsform } from './types';
import './einstellungen.css';

const euroText = (v: number | null) => (v == null ? '' : v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const euroOf = (t: string) => {
  const v = parseEuro(t);
  return v === undefined ? NaN : v;
};

// Angaben des Mandanten auf seinen Rechnungen (Absender, Steuernummer, Bank, Rechtsform mit ihren Pflichtangaben).
// Beim ersten Mal aus den Firmendaten vorgeschlagen. Mit #kleinunternehmer in der Adresse (Hinweis in der Übersicht,
// Meldung zur Kleinunternehmer-Grenze) sind die Umsätze außerhalb dieser Seite gleich aufgeklappt.
export function AbsenderForm({
  firmId,
  initial,
  today,
  onSaved,
  nurLesen = false,
}: {
  firmId: string;
  initial: Absender;
  // heute laut Server (JJJJ-MM-TT): das Jahr für die Kleinunternehmer-Angaben
  today: string;
  onSaved(): Promise<void>;
  // Billtoro: Team-Mitglied, nur der Inhaber ändert die Angaben (tarifHinweis.tsx)
  nurLesen?: boolean;
}) {
  const year = Number(today.slice(0, 4));
  // Kleinunternehmer: nur ein Haken (Sam 13.10.), die App zählt die eigenen Rechnungen selbst. Was außerhalb dieser
  // Seite war, ist freiwillig und gilt für das Jahr, in dem es gespeichert ist (im neuen Jahr leer).
  const kuAlt = initial.kuJahr === year;
  const [f, setF] = useState(() => ({
    ...initial,
    email: initial.email ?? '',
    phone: initial.phone ?? '',
    taxNumber: initial.taxNumber ?? '',
    vatId: initial.vatId ?? '',
    bic: initial.bic ?? '',
    bankName: initial.bankName ?? '',
    paymentDays: String(initial.paymentDays),
    kuGruendung: kuAlt && initial.kuGruendung,
    kuVorjahr: kuAlt ? euroText(initial.kuVorjahr) : '',
    kuExtra: kuAlt && initial.kuExtra ? euroText(initial.kuExtra) : '',
    rechtsform: (initial.rechtsform ?? '') as Rechtsform | '',
    sitz: initial.sitz ?? '',
    registergericht: initial.registergericht ?? '',
    registernr: initial.registernr ?? '',
    vertretenDurch: initial.vertretenDurch ?? '',
  }));
  const form = formInfo(f.rechtsform);
  // Rechtsform gehört in den Namen (z. B. „GmbH“): Hinweis schon beim Tippen, der Server prüft beim Speichern
  const nameOhneZusatz = !!form?.zusatz && f.name.trim() !== '' && !form.zusatz.test(f.name);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // freiwillige Umsatz-Angaben zugeklappt, außer es steht schon etwas darin oder die Seite wurde dafür geöffnet
  const kuZiel = useLocation().hash === '#kleinunternehmer';
  const [kuOffen, setKuOffen] = useState(() => kuZiel || f.kuGruendung || f.kuVorjahr !== '' || f.kuExtra !== '');
  useEffect(() => {
    if (!kuZiel) return;
    setKuOffen(true);
    document.getElementById('kleinunternehmer')?.scrollIntoView({ block: 'center' });
  }, [kuZiel]);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (nurLesen) return;
    setError(null);
    // ein Tippfehler im Umsatz darf nicht als 0 € gespeichert werden
    const vorjahr = f.kleinunternehmer && !f.kuGruendung && f.kuVorjahr.trim() !== '' ? euroOf(f.kuVorjahr) : null;
    const extra = f.kleinunternehmer && f.kuExtra.trim() !== '' ? euroOf(f.kuExtra) : 0;
    if (Number.isNaN(vorjahr) || Number.isNaN(extra)) {
      setError(`Bitte den Umsatz ${Number.isNaN(vorjahr) ? year - 1 : `${year} außerhalb dieser Seite`} prüfen, z. B. 18.500,00.`);
      return;
    }
    setBusy(true);
    try {
      await api.put(`/api/rechnungstool/${firmId}/absender`, {
        ...f,
        paymentDays: f.paymentDays.trim() === '' ? NaN : Number(f.paymentDays),
        kuVorjahr: vorjahr,
        kuExtra: extra,
        rechtsform: f.rechtsform || null,
      });
      await onSaved();
    } catch (err) {
      setError(errorText(err));
      // Meldung zur Kleinunternehmer-Grenze: Der Ausweg (Umsatz des Vorjahres) steht in den Umsätzen außerhalb
      if (err instanceof ApiError && (err.code === 'ku_grenze' || err.code === 'ku_vorjahr')) setKuOffen(true);
      setBusy(false);
    }
  }

  return (
    <form className="rt-card" onSubmit={submit} noValidate>
      <h1>Ihre Angaben auf der Rechnung</h1>
      {!initial.saved && (
        <p className="muted">Einmal eintragen, dann stehen sie auf jeder Rechnung. Wir haben schon vorausgefüllt, was wir von Ihrer Firma wissen.</p>
      )}
      {initial.saved && !f.rechtsform && (
        <div className="notice warn rt-rf-banner">
          <b>Bitte wählen Sie Ihre Rechtsform.</b> Bei GmbH, UG, AG und Firmen im Handelsregister gehören Sitz, Registergericht
          und Geschäftsführer unten auf jede Rechnung. Wir setzen sie dann automatisch ein.
        </div>
      )}
      <NurLesen an={nurLesen}>
        <div className="rt-grid">
          <label className="field rt-span2">
            <span>Firmenname</span>
            <input value={f.name} onChange={set('name')} autoComplete="organization" aria-invalid={nameOhneZusatz || undefined} />
            {nameOhneZusatz && <small className="err">Die Rechtsform gehört in den Namen, z. B. „{form!.beispiel}“.</small>}
          </label>
          <label className="field rt-span2">
            <span>Rechtsform</span>
            <select value={f.rechtsform} onChange={set('rechtsform')}>
              <option value="">Bitte wählen</option>
              {RECHTSFORMEN.map(([k, info]) => (
                <option key={k} value={k}>
                  {info.label}
                </option>
              ))}
            </select>
          </label>
          {form && (form.register || form.vertretung) && (
            <div className="rt-span4 rt-rf">
              <p className="rt-rf-t">Steht unten auf jeder Rechnung</p>
              <div className="rt-grid">
                {form.register && (
                  <>
                    <label className="field">
                      <span>Sitz{form.register === 'frei' ? ' (freiwillig)' : ''}</span>
                      <input value={f.sitz} onChange={set('sitz')} placeholder="z. B. Hamburg" maxLength={80} />
                    </label>
                    <label className="field rt-span2">
                      <span>Registergericht{form.register === 'frei' ? ' (freiwillig)' : ''}</span>
                      <input value={f.registergericht} onChange={set('registergericht')} placeholder="z. B. Amtsgericht Hamburg" maxLength={80} />
                    </label>
                    <label className="field">
                      <span>Registernummer{form.register === 'frei' ? ' (freiwillig)' : ''}</span>
                      <input value={f.registernr} onChange={set('registernr')} placeholder={`z. B. ${form.hrPrefix} 12345`} autoComplete="off" maxLength={40} />
                    </label>
                  </>
                )}
                {form.vertretung && (
                  <label className="field rt-span4">
                    <span>{form.vertretung.label}</span>
                    {form.vertretung.lang ? (
                      <textarea value={f.vertretenDurch} onChange={set('vertretenDurch')} placeholder={form.vertretung.placeholder} rows={2} maxLength={300} />
                    ) : (
                      <input value={f.vertretenDurch} onChange={set('vertretenDurch')} placeholder={form.vertretung.placeholder} maxLength={300} />
                    )}
                    {form.vertretung.hint && <small>{form.vertretung.hint}</small>}
                  </label>
                )}
              </div>
            </div>
          )}
          <label className="field rt-span2">
            <span>Straße und Hausnummer</span>
            <input value={f.street} onChange={set('street')} autoComplete="street-address" />
          </label>
          <label className="field">
            <span>PLZ</span>
            <input value={f.zip} onChange={set('zip')} inputMode="numeric" autoComplete="postal-code" />
          </label>
          <label className="field">
            <span>Ort</span>
            <input value={f.city} onChange={set('city')} autoComplete="address-level2" />
          </label>
          <label className="field">
            <span>E-Mail</span>
            <input value={f.email} onChange={set('email')} type="email" autoComplete="email" />
          </label>
          <label className="field">
            <span>Telefon</span>
            <input value={f.phone} onChange={set('phone')} type="tel" autoComplete="tel" />
          </label>
          <label className="field">
            <span>Steuernummer</span>
            <input value={f.taxNumber} onChange={set('taxNumber')} />
          </label>
          <label className="field">
            <span>USt-IdNr.</span>
            <input value={f.vatId} onChange={set('vatId')} />
            <small>Eins von beiden muss auf jeder Rechnung stehen.</small>
          </label>
          <label className="field rt-span2">
            <span>Kontoinhaber</span>
            <input value={f.accountHolder} onChange={set('accountHolder')} />
          </label>
          <label className="field rt-span2">
            <span>IBAN</span>
            <input value={f.iban} onChange={set('iban')} autoComplete="off" />
          </label>
          <label className="field">
            <span>BIC (optional)</span>
            <input value={f.bic} onChange={set('bic')} autoComplete="off" />
          </label>
          <label className="field">
            <span>Bank (optional)</span>
            <input value={f.bankName} onChange={set('bankName')} />
          </label>
          <label className="field">
            <span>Zahlungsziel in Tagen</span>
            <input value={f.paymentDays} onChange={set('paymentDays')} inputMode="numeric" />
            <small>Vorschlag für neue Rechnungen</small>
          </label>
        </div>
        <label className="check" id="kleinunternehmer">
          <input type="checkbox" checked={f.kleinunternehmer} onChange={(e) => setF({ ...f, kleinunternehmer: e.target.checked })} />
          <span>Ich bin Kleinunternehmer</span>
        </label>
        {f.kleinunternehmer && (
          <details className="rt-ku-mehr" open={kuOffen} onToggle={(e) => setKuOffen((e.target as HTMLDetailsElement).open)}>
            <summary>Umsatz außerhalb dieser Seite (optional)</summary>
            <label className="check">
              <input type="checkbox" checked={f.kuGruendung} onChange={(e) => setF({ ...f, kuGruendung: e.target.checked })} />
              <span>Ich habe {year} gegründet</span>
            </label>
            <div className="rt-grid rt-grid2">
              {!f.kuGruendung && (
                <label className="field">
                  <span>Umsatz {year - 1} insgesamt</span>
                  <input value={f.kuVorjahr} onChange={set('kuVorjahr')} inputMode="decimal" placeholder="z. B. 18.500,00" />
                </label>
              )}
              <label className="field">
                <span>Umsatz {year} außerhalb dieser Seite</span>
                <input value={f.kuExtra} onChange={set('kuExtra')} inputMode="decimal" placeholder="0,00" />
              </label>
            </div>
          </details>
        )}
      </NurLesen>
      {error && <div className="notice bad">{error}</div>}
      <div className="rt-actions">
        {initial.saved && (
          <Link to="/rechnung" className="btn">
            Abbrechen
          </Link>
        )}
        {!nurLesen && (
          <button type="submit" className="btn primary" disabled={busy}>
            Speichern
          </button>
        )}
      </div>
    </form>
  );
}
